import type { SuperHubStore } from '..';
import type { StateCreator } from 'zustand';

import { actionLog } from '@core/store/util';

interface SuperHubLinterSliceState {
  /**
   * Whether the linter is currently running
   */
  isLoading: boolean;
  /**
   * Results from the last linter run, or null if no run has been performed yet
   * @todo: mocked type for now, replace with real type from API when available
   */
  results: {
    errors: number;
    warnings: number;
    score: string;
  } | null;

  /**
   * Which view to show in the results UI. Can be one of:
   * - 'errors': show number of errors
   * - 'warnings': show number of warnings
   * - 'score': show style guide score
   * - null: no view selected (e.g. immediately after running the linter)
   */
  resultsView: 'errors' | 'score' | 'warnings' | null;
}

interface SuperHubLinterSliceAction {
  /**
   * Runs the linter on the current document. This is an async action that
   * returns a promise that resolves when the linter has completed.
   */
  run: () => Promise<void>;

  /**
   * Sets which view to show in the linter results UI.
   */
  setResultsView: (view: SuperHubLinterSliceState['resultsView']) => void;
}

export interface SuperHubLinterSlice {
  /**
   * State slice containing fields and actions that are relevant when using
   * the AI-powered linter for SuperHub doc pages.
   */
  linter: SuperHubLinterSliceAction & SuperHubLinterSliceState;
}

const initialState: SuperHubLinterSliceState = {
  isLoading: false,
  results: null,
  resultsView: null,
};

/**
 * SuperHubLinter state slice containing fields related to linting doc pages.
 */
export const createSuperHubLinterSlice: StateCreator<
  SuperHubLinterSlice & SuperHubStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  SuperHubLinterSlice
> = set => ({
  linter: {
    ...initialState,

    run: () => {
      set(
        state => {
          state.linter.isLoading = true;
        },
        false,
        actionLog('linter.run.pending'),
      );

      // Simulate async linter run
      return new Promise(resolve => {
        const randomResult = () => Math.floor(Math.random() * 10) + 1; // random number between 1 and 10

        setTimeout(() => {
          set(
            state => {
              state.linter.isLoading = false;
              state.linter.resultsView = null;
              state.linter.results = {
                errors: randomResult(),
                warnings: randomResult(),
                score: `${randomResult()}/10`,
              };
            },
            false,
            actionLog('linter.run.success'),
          );
          resolve();
        }, 2000);
      });
    },

    setResultsView: view => {
      set(
        state => {
          state.linter.resultsView = view;
        },
        false,
        actionLog('linter.setResultsView', view),
      );
    },
  },
});
